import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useAuth } from '@/modules/auth/AuthContext';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { useVenueContext } from '@/modules/venue/VenueContext';
import { ApiError } from '@/shared/api-client/workspace';
import { Button } from '@/shared/ui/components/button';
import { Checkbox } from '@/shared/ui/components/checkbox';
import { Input } from '@/shared/ui/components/input';
import { Label } from '@/shared/ui/components/label';
import { useUpdateVenueOperatingHours, useVenueOperatingHours } from './operatingHours';
import {
  toEditableWeekdayHours,
  toRequestWeekdayHours,
  type EditableWeekdayHours,
  validateEditableWeekdayHours,
} from './operatingHoursForm';

export function operatingHoursErrorMessage(
  t: ReturnType<typeof useTranslation>['t'],
  error: unknown,
): string {
  if (error instanceof ApiError && error.code) {
    const key = `venues.operatingHours.errors.${error.code}`;
    const translated = t(key);
    if (translated !== key) return translated;
  }
  return t('venues.operatingHours.errors.generic');
}

export function OperatingHoursPage() {
  const { t } = useTranslation('common');
  const { venues } = useAuth();
  const { venueId } = useVenueContext();
  const auth = useAuthorization();
  const canManage = auth.can('venue.operating_hours.manage', { kind: 'venue', venueId });
  const venueName = venues.find((venue) => venue.id === venueId)?.name ?? venueId;

  const { data, isLoading, isError } = useVenueOperatingHours(venueId);
  const update = useUpdateVenueOperatingHours(venueId);

  const [weekdayHours, setWeekdayHours] = useState<EditableWeekdayHours[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setWeekdayHours(data.weekdayHours.map(toEditableWeekdayHours));
  }, [data]);

  function updateRow(
    weekday: EditableWeekdayHours['weekday'],
    edit: Partial<EditableWeekdayHours>,
  ) {
    setWeekdayHours((current) =>
      current.map((row) => (row.weekday === weekday ? { ...row, ...edit } : row)),
    );
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    const validationError = validateEditableWeekdayHours(weekdayHours);
    if (validationError) {
      setError(t(`venues.operatingHours.errors.${validationError}`));
      return;
    }

    try {
      await update.mutateAsync({
        weekdayHours: weekdayHours.map(toRequestWeekdayHours),
      });
      toast.success(t('venues.operatingHours.toast.updated'));
    } catch (caught) {
      setError(operatingHoursErrorMessage(t, caught));
    }
  }

  return (
    <section className="grid gap-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">{t('venues.operatingHours.pageTitle')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('venues.operatingHours.scopedDescription', { name: venueName })}
        </p>
      </div>

      {isLoading && <p className="text-muted-foreground">{t('venues.operatingHours.loading')}</p>}
      {isError && (
        <p className="text-destructive">{t('venues.operatingHours.errors.loadFailed')}</p>
      )}

      {weekdayHours.length > 0 && (
        <form onSubmit={submit} className="grid max-w-3xl gap-4">
          {weekdayHours.map((row) => {
            const key = row.weekday.toLowerCase();
            const dayLabel = t(`venues.operatingHours.weekdays.${key}`);
            const openId = `${key}-open-time`;
            const closeId = `${key}-close-time`;
            const closedId = `${key}-closed`;
            return (
              <div
                key={row.weekday}
                className="grid gap-3 rounded-lg border p-4 md:grid-cols-[140px_1fr_1fr_auto] md:items-end"
              >
                <div className="font-medium">{dayLabel}</div>
                <div className="grid gap-2">
                  <Label htmlFor={openId}>
                    {t('venues.operatingHours.fields.openTime', { day: dayLabel })}
                  </Label>
                  <Input
                    id={openId}
                    type="time"
                    disabled={!canManage || row.closed}
                    value={row.openTime}
                    onChange={(event) => updateRow(row.weekday, { openTime: event.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor={closeId}>
                    {t('venues.operatingHours.fields.closeTime', { day: dayLabel })}
                  </Label>
                  <Input
                    id={closeId}
                    type="time"
                    disabled={!canManage || row.closed}
                    value={row.closeTime}
                    onChange={(event) => updateRow(row.weekday, { closeTime: event.target.value })}
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Checkbox
                    id={closedId}
                    disabled={!canManage}
                    checked={row.closed}
                    onCheckedChange={(checkedState) =>
                      updateRow(row.weekday, {
                        closed: checkedState === true,
                        openTime: checkedState === true ? '' : row.openTime,
                        closeTime: checkedState === true ? '' : row.closeTime,
                      })
                    }
                  />
                  <Label htmlFor={closedId}>
                    {t('venues.operatingHours.fields.closed', { day: dayLabel })}
                  </Label>
                </div>
              </div>
            );
          })}
          {error && <p className="text-sm text-destructive">{error}</p>}
          {canManage && (
            <div>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending
                  ? t('venues.operatingHours.form.submitting')
                  : t('venues.operatingHours.form.save')}
              </Button>
            </div>
          )}
        </form>
      )}
    </section>
  );
}
